import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setAnnouncement } from 'store/settings/announcementSlice'
import { apiGetAnnouncement, apiUpdateAnnouncement } from 'services/SettingService'

// Kept in sync with SettingController::ANNOUNCEMENT_MAX_LENGTH, which
// re-checks it server-side against the visible text (not the HTML).
export const ANNOUNCEMENT_MAX_LENGTH = 160

// Read-only: just the current value, no fetch side-effect. Use this in the
// announcement bar, which remounts on every route change.
//
// The `?.` on the sub-slice is load-bearing, not defensive habit: a browser
// that persisted `settings` before this slice existed rehydrates without it,
// and combineReducers only refills the key on the *next* dispatched action --
// so the first render after rehydrate can legitimately see it missing. The
// store's autoMergeLevel2 reconciler is what prevents that now, but this keeps
// a stale cache from blanking the entire app (there is no error boundary).
export function useAnnouncementValue() {
    return useSelector((state) => state.settings.announcement?.value) || ''
}

// Fetch-on-mount + read + update. Only call this from the top-level Layout
// (the one place that triggers the initial load) and the settingsdb card (the
// one place that writes) -- see the same rule for useSiteName.
function useAnnouncement() {

    const dispatch = useDispatch()

    const loaded = useSelector((state) => state.settings.announcement?.loaded)
    const announcement = useAnnouncementValue()

    useEffect(() => {
        if (loaded) {
            return
        }
        apiGetAnnouncement().then(resp => {
            dispatch(setAnnouncement(resp.data.announcement || ''))
        }).catch(() => {})
    }, [loaded, dispatch])

    const updateAnnouncement = async (html) => {
        const resp = await apiUpdateAnnouncement(html)
        dispatch(setAnnouncement(resp.data.announcement || ''))
        return resp.data.announcement || ''
    }

    return { announcement, loaded, updateAnnouncement }
}

export default useAnnouncement
